import React, { FC, useCallback, useContext, useEffect } from 'react';
import { Alert, AlertIcon, Badge, Box, Flex, Heading, Img, Text, useColorModeValue as mode } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import VerifyCodeForm from '../components/VerifyCodeForm';
import SignUpForm from '../components/SignUpForm';
import { AuthStateContext, AuthStatus } from '../contexts/AuthContext';
import Page from '../components/Page';
import Loading from '../components/Loading';

const Register: FC = () => {
  const router = useRouter();
  const { isAuthenticated, sessionStatus, error } = useContext(AuthStateContext);

  // Redirect to account after signup
  useEffect(() => {
    if (isAuthenticated) {
      router.push('/account');
    }
  }, [isAuthenticated]);

  const getAuthStep = useCallback(() => {
    let step: React.ReactNode;
    switch (sessionStatus) {
      // Sign-up form
      case AuthStatus.ANONYMOUS:
      case AuthStatus.PENDING_SIGN_UP:
      case AuthStatus.SIGN_UP_ERROR:
        step = <SignUpForm />;
        break;

      // Verify sign-up
      case AuthStatus.SIGN_UP_CONFIRM:
      case AuthStatus.PENDING_SIGN_UP_CONFIRM:
      case AuthStatus.SIGN_UP_CONFIRM_ERROR:
        step = <VerifyCodeForm type='CONFIRM_SIGN_UP' />;
        break;

      default:
        step = <Loading h='auto' />;
    }

    return step;
  }, [sessionStatus]);

  return (
    <Page
      seoOptions={{
        title: 'Join Vitamin Club - Personalized Family Wellness',
        description:
          'Join Vitamin Club and discover personalised vitamin plans for your family. Sign up to enhance your' +
          ' health with dietitian-certified supplements.',
        canonical: 'https://vitaminclub.co.za/sign-up/'
      }}
      maxW='100%'
      p={0}
      m={0}
    >
      <>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          overflow='hidden'
          minH='100vh'
          height='100%'
          bg={mode('gray.50', 'inherit')}
        >
          <Box display={{ base: 'none', lg: 'block' }} flex='1' bg='gray.500' color='white' px='20' pt='32'>
            <Badge bg='blackAlpha.200' px='4' py='1' rounded='md' letterSpacing='wide' color='whiteAlpha.900'>
              Vitamins should be simple!
            </Badge>
            <Text
              mt='6'
              fontWeight='extrabold'
              fontSize={{ base: '2xl', lg: '3xl' }}
              maxW='sm'
              letterSpacing='tight'
              lineHeight='normal'
            >
              Sign up for a free account today
            </Text>
            <Text mt='5' maxW='md' fontSize='lg'>
              Track your family's vitamin needs in one place. Sign-up is quick and checkout even quicker.
            </Text>
            <Box m='10' position='relative' insetEnd='6'>
              <Img alt='' src='/images/undraw-happy@2x.png' />
            </Box>
          </Box>

          <Box overflowY='auto' flex='1' py={{ base: '10', md: '16' }} px={{ base: '6', md: '10' }}>
            <Box maxW='sm' mx='auto'>
              <Box textAlign='center' mb={{ base: '4', md: '6' }}>
                <Heading as='h1' size='xl' fontWeight='extrabold' letterSpacing='tight'>
                  {sessionStatus !== AuthStatus.SIGN_UP_CONFIRM && 'Sign up for an account'}
                  {sessionStatus === AuthStatus.SIGN_UP_CONFIRM && 'Verify account email'}
                </Heading>
              </Box>

              {/* Errors */}
              {sessionStatus === AuthStatus.SIGN_UP_ERROR ||
                (sessionStatus === AuthStatus.SIGN_UP_CONFIRM_ERROR && (
                  <Alert status='error' mb='4' borderRadius='md'>
                    <AlertIcon />
                    {error?.message}
                  </Alert>
                ))}

              {/* Auth steps */}
              {getAuthStep()}
            </Box>
          </Box>
        </Flex>
      </>
    </Page>
  );
};

export default Register;
